import { Injectable } from "@angular/core";
import { Misc } from "../../global-misc/misc";
import { GlobalMessageService } from "../../global-services/global-message.service";
import { TreatmentModel } from "../../global-data-models/treatment.model";
import { TreatmentService } from "../../global-services/treatment.service";
import { PatientService } from "../../global-services/patient.service";
import { PatientModel } from "../../global-data-models/patient.model";
import { GlobalLoadingService } from "../../global-services/global-loading.service";

export interface TreatmentWithAgeModel extends TreatmentModel {
    ageAtStartString?: string;
    treatmentMethodName?: string;
}

@Injectable({ providedIn: "root" })
export class TreatmentsViewFacade {
    public constructor(
        private readonly messageService: GlobalMessageService,
        private readonly treatmentService: TreatmentService,
        private readonly patientService: PatientService,
        private readonly loadingService: GlobalLoadingService
    ) { }

    public async deleteTreatment(orgId: string, patientId: string, treatmentId: string) {
        this.loadingService.show();

        try {
            await this.treatmentService.deletePatientTreatment(orgId, patientId, treatmentId);
            this.messageService.showSuccess("Delete Treatment Success", "Treatment deleted successfully!");
        } catch (error: unknown) {
            const errMsg: string = Misc.getErrorMessage(error);
            this.messageService.showError("Delete Treatment Error", errMsg);
        } finally {
            this.loadingService.hide();
            console.log("done deleting treatment from db");
        }
    }

    public getHumanReadableDate(date: Date | string): string {
        const dateObj = typeof date === 'string' ? new Date(date) : date;
        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        }).format(dateObj);
    }

    private getPatientAgeStringAtDate(patient: PatientModel, date: Date | string): string {
        try {
            const patientDob = new Date(patient.dateOfBirth);
            const treatmentDate = typeof date === 'string' ? new Date(date) : date;

            const yearDiff = treatmentDate.getFullYear() - patientDob.getFullYear();
            const monthDiff = treatmentDate.getMonth() - patientDob.getMonth();

            let years: number = yearDiff;
            let months: number = monthDiff;

            if (monthDiff < 0) {
                years = yearDiff - 1;
                months = monthDiff + 12;
            }

            return `${years} year${years === 1 ? '' : 's'}`;
        } catch (error: unknown) {
            const errorMessage: string = Misc.getErrorMessage(error);
            this.messageService.showError("Get Patient Error", errorMessage);
            return "PATIENT_AGE_AT_DATE_ERROR";
        }
    }

    public async getTreatmentsWithAge(orgId: string, patientId: string): Promise<TreatmentWithAgeModel[]> {
        this.loadingService.show();
        try {
            const rawTreatments: TreatmentModel[] = await this.treatmentService.getPatientTreatments(orgId, patientId);
            const patient: PatientModel = await this.patientService.getPatient(orgId, patientId);
            const treatmentMethods = await this.treatmentService.getTreatmentMethods();

            return rawTreatments.map((treatment) => {
                const treatmentMethod = treatmentMethods.find(method => method.id === treatment.treatment_method_id);
                return {
                    ...treatment,
                    ageAtStartString: this.getPatientAgeStringAtDate(patient, treatment.start_date),
                    treatmentMethodName: treatmentMethod?.displayName || 'Unknown Treatment'
                };
            });
        } catch (error: unknown) {
            const errorMessage: string = Misc.getErrorMessage(error);
            this.messageService.showError("Get Treatments Error", errorMessage);
            return [] as TreatmentModel[];
        } finally {
            this.loadingService.hide();
        }
    }
}
